import { Injectable, computed, signal } from '@angular/core';
import { Company } from '../../model';

/**
 * Carries the server's write rule for the selected company into the UI, and nothing else.
 *
 * `Company.mayWrite` is the single write rule; the frontend only mirrors it. Every read-only state
 * in the app reads this one signal, so the rule is evaluated once instead of being re-derived —
 * and mis-derived — per component. Never derive write rights from `demoData`: that is an extra
 * READ grant which leaves the owning team full write access.
 *
 * Deliberately its own service rather than a field on `AppState`. Consumers reach from page
 * components down to leaf buttons, and `AppState` pulls in `AppAuthService` → `HttpClient` plus a
 * login subscription that fetches companies. Injecting that graph into a leaf component to answer
 * one boolean makes every spec of every read-only-aware component provide HTTP. This class has no
 * dependencies at all, so it costs a consumer's tests nothing.
 *
 * `AppState.selectCompanySite` is the ONLY writer — one rule, one place it is set, fed by the same
 * `companyDetailResolver` that hands the company to the pages.
 */
@Injectable({ providedIn: 'root' })
export class CompanyWriteAccess {
  private readonly mayWrite = signal<boolean | undefined>(undefined);

  /**
   * True only for an explicit `mayWrite: false`.
   *
   * `=== false`, never `!== true`: the flag is optional and the server refuses the write
   * regardless, so a missing field (older server, a fixture predating the flag) must not lock
   * anyone out. Same reason `companyWritableGuard` is not fail-closed.
   *
   * ⚠ This describes the CURRENTLY SELECTED company, not "the company of this route". Identical
   * for every consumer under `:companyId/site/:siteId`, which is where all of them live — a caller
   * from outside that subtree would get an answer about the wrong company. Route activation is a
   * different question again: `companyWritableGuard` runs before this navigation's resolver has
   * fed the selection, so it reads `pathFromRoot` and must keep doing so.
   */
  readonly readOnly = computed(() => this.mayWrite() === false);

  /** Called by `AppState.selectCompanySite`; not part of the consumer-facing surface. */
  select(company: Company | undefined): void {
    this.mayWrite.set(company?.mayWrite);
  }
}
